"use client";

import { ChevronDownIcon, FilterIcon, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import type { ProjectsSortBy } from "@/lib/get-projects";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { INITIAL_PAGE_SIZE, PAGE_SIZE } from "@/lib/get-projects";
import { cn } from "@/lib/utils";
import { useProjectsList } from "@/hooks/use-projects-list";
import { imagineRoutingStore } from "@/wrapper/imagine-routing.store";
import { useRef } from "react";
import { ProjectCard, projectItemCn } from "./project-card";

const sortByOptions = [
  {
    label: "Last edited",
    value: "updatedAt",
  },
  {
    label: "Created",
    value: "createdAt",
  },
];

export function FeaturedProjectsSection() {
  const navigate = imagineRoutingStore.use.navigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isInitialLoading,
    isPageLoading,
    projects,
    totalProjects,
    loadMoreProjects,
  } = useProjectsList("featured-projects", containerRef);

  return (
    <div
      className="mx-auto max-w-6xl py-12 px-4 w-full min-w-0"
      ref={containerRef}
    >
      <h2 className="mb-4 text-2xl font-normal text-foreground leading-tight">
        Get inspired
      </h2>

      {/* Projects grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 w-full">
        {isInitialLoading ? (
          <ProjectsSkeleton count={INITIAL_PAGE_SIZE} />
        ) : (
          projects?.map((project) => (
            <div
              key={project.id}
              className="min-w-0 w-full flex justify-center sm:justify-start"
            >
              <ProjectCard
                project={project}
                onClick={() =>
                  navigate(
                    {
                      id: "view",
                      props: { projectId: project.id, region: "fra" },
                    },
                    {
                      animate: {
                        promptSection: {
                          delay: 400,
                        },
                        projectsSection: {
                          delay: 0,
                        },
                        navigationDelay: 200,
                      },
                    },
                  )
                }
              />
            </div>
          ))
        )}

        {isPageLoading && <ProjectsSkeleton count={PAGE_SIZE} />}
      </div>
      {!projects?.length && !isInitialLoading && (
        <div className="w-full my-4 rounded-2xl shadow-[-76px_209px_62px_0_rgba(0,0,0,0.00),-49px_134px_57px_0_rgba(0,0,0,0.00),-12px_33px_36px_0_rgba(0,0,0,0.01),-3px_8px_20px_0_rgba(0,0,0,0.01)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0.25)] bg-gradient-to-b from-[#F5F5F7] to-[#EAEAEC] dark:from-[#36363C] dark:to-[#2C2C30] p-[1px]">
          <div className="bg-background rounded-2xl flex flex-col items-center gap-1.5 p-8">
            <p className="text-xl text-foreground font-normal">
              Could not find any featured projects
            </p>
            <p className="text-sm text-muted-foreground font-light">
              Keep an eye out!
            </p>
          </div>
        </div>
      )}
      {projects?.length > 0 &&
        projects.length < totalProjects &&
        !isPageLoading && (
          <div className="flex justify-center w-full mt-6">
            <Button
              variant="outline"
              onClick={() => loadMoreProjects()}
              disabled={isPageLoading}
              loading={isPageLoading}
            >
              Show more
            </Button>
          </div>
        )}
    </div>
  );
}

function AnimatedProjectWrapper({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(projectItemCn, className)}>{children}</div>;
}

function ProjectsSkeleton({ count }: { count: number }) {
  return Array.from({ length: count }).map((_, i) => (
    <AnimatedProjectWrapper
      key={i}
      className={cn(
        "h-70 flex flex-col rounded-2xl p-[1px] animate-pulse gap-y-2",
        "bg-muted/30 dark:bg-muted/10",
      )}
    >
      <Skeleton key={i} className="flex-1 w-full" />
      <div className="rounded-md my-1 space-y-3">
        <Skeleton className="h-6 w-3/5" />
        <Skeleton className="h-4 w-2/5" />
      </div>
    </AnimatedProjectWrapper>
  ));
}
